import { useState } from 'react';
import { useClient } from 'urql';
import {
  TimetableVersionConstraintsDocument,
  TimetableVersionConstraintsQuery,
  TimetableVersionConstraintsQueryVariables,
  TimetableVersionResourceType,
} from '../types/planung-graphql-client-defs';

type ConditionsBlob = Record<string, unknown>;

type UseConditionsDuplicationArgs = {
  versionUuid: string;
  /** Art der Ressourcen, zwischen denen kopiert wird. */
  resourceType: TimetableVersionResourceType;
  /** Persists a blob onto one target resource; resolves to success. */
  write: (targetUuid: string, blob: ConditionsBlob) => Promise<boolean>;
};

/**
 * Copies conditions from one resource onto others of the same type. `duplicateAll` overwrites each
 * target's whole blob (like the availability duplicate); `duplicateFields` read-modify-writes each
 * target so a single copied constraint merges in without clearing the target's other conditions.
 */
export const useConditionsDuplication = ({ versionUuid, resourceType, write }: UseConditionsDuplicationArgs) => {
  const client = useClient();
  const [loading, setLoading] = useState(false);

  const readBlob = async (uuid: string): Promise<ConditionsBlob> => {
    const result = await client
      .query<TimetableVersionConstraintsQuery, TimetableVersionConstraintsQueryVariables>(
        TimetableVersionConstraintsDocument,
        { versionUuid, resourceType, resourceUuids: [uuid] },
        { requestPolicy: 'network-only' },
      )
      .toPromise();
    const raw = result.data?.timetableVersionConstraints[0]?.constraints;
    try {
      return raw ? (JSON.parse(raw) as ConditionsBlob) : {};
    } catch {
      return {};
    }
  };

  const duplicateAll = async (sourceBlob: ConditionsBlob, targetUuids: string[]) => {
    setLoading(true);
    for (const uuid of targetUuids) {
      await write(uuid, sourceBlob);
    }
    setLoading(false);
  };

  const duplicateFields = async (sourceBlob: ConditionsBlob, fields: string[], targetUuids: string[]) => {
    setLoading(true);
    for (const uuid of targetUuids) {
      const target = await readBlob(uuid);
      for (const field of fields) {
        if (sourceBlob[field] == null) {
          delete target[field];
        } else {
          target[field] = sourceBlob[field];
        }
      }
      await write(uuid, target);
    }
    setLoading(false);
  };

  return { loading, duplicateAll, duplicateFields };
};
